import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowRight, MapPin, Calendar, ChevronRight, Building2, Users, Truck, Package, ShieldCheck, Info } from "lucide-react";
import api from "../lib/api";
import AddressAutocomplete from "../components/AddressAutocomplete";

/**
 * Page landing SEO — rend région / département / ville selon les params de route.
 * URLs :
 *   /demenagement/:regionSlug
 *   /demenagement/:regionSlug/:deptSlugNumero
 *   /demenagement/:regionSlug/:deptSlugNumero/:citySlug
 */
export default function SeoLanding() {
  const { regionSlug, deptSlugNumero, citySlug } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [departure, setDeparture] = useState("");
  const [arrival, setArrival] = useState("");
  const [movingDate, setMovingDate] = useState("");

  useEffect(() => {
    let endpoint;
    if (citySlug) endpoint = `/seo/pages/city/${regionSlug}/${deptSlugNumero}/${citySlug}`;
    else if (deptSlugNumero) endpoint = `/seo/pages/department/${regionSlug}/${deptSlugNumero}`;
    else endpoint = `/seo/pages/region/${regionSlug}`;

    api.get(endpoint)
      .then((r) => {
        setPage(r.data);
        // Head SEO
        document.title = r.data.title;
        setMetaTag("description", r.data.meta_description);
        setMetaTag("robots", r.data.seo_status === "index" ? "index,follow" : "noindex,follow");
        setLinkTag("canonical", `${window.location.origin}${r.data.slug_path}`);
        // Structured data
        injectSchema(r.data);
      })
      .catch(() => setNotFound(true));
  }, [regionSlug, deptSlugNumero, citySlug]);

  const startQuote = (e) => {
    e?.preventDefault();
    const params = new URLSearchParams();
    if (departure) params.set("dep", departure);
    if (arrival) params.set("arr", arrival);
    if (movingDate) params.set("date", movingDate);
    navigate(`/devis${params.toString() ? "?" + params.toString() : ""}`);
  };

  if (notFound) {
    return (
      <div className="container mx-auto px-6 py-20 text-center" data-testid="seo-not-found">
        <h1 className="font-heading font-extrabold text-3xl mb-4 text-[#2C3E50]">Page introuvable</h1>
        <Link to="/" className="btn-primary">Retour à l'accueil</Link>
      </div>
    );
  }
  if (!page) return <div className="container mx-auto px-6 py-20 text-center text-sm text-[#6B7280]">Chargement…</div>;

  const ctx = page.ctx || {};
  const type = page.type;

  return (
    <div className="animate-fade-up" data-testid={`seo-page-${type}`}>
      {/* Breadcrumb */}
      <div className="container mx-auto px-6 lg:px-10 pt-8">
        <nav className="text-xs text-[#6B7280] flex items-center gap-2 flex-wrap" aria-label="Breadcrumb" data-testid="breadcrumb">
          <Link to="/" className="hover:text-[#2BD391]">Accueil</Link>
          <ChevronRight className="w-3 h-3" />
          <Link to={`/demenagement/${page.region_slug}`} className={type === "region" ? "text-[#2C3E50] font-semibold" : "hover:text-[#2BD391]"}>
            {ctx.region_name || "Région"}
          </Link>
          {page.dept_slug_numero && (
            <>
              <ChevronRight className="w-3 h-3" />
              <Link
                to={`/demenagement/${page.region_slug}/${page.dept_slug_numero}`}
                className={type === "department" ? "text-[#2C3E50] font-semibold" : "hover:text-[#2BD391]"}
              >
                {ctx.dept_name || "Département"}
              </Link>
            </>
          )}
          {page.city_slug && (
            <>
              <ChevronRight className="w-3 h-3" />
              <span className="text-[#2C3E50] font-semibold">{ctx.city}</span>
            </>
          )}
        </nav>
      </div>

      {/* HERO */}
      <section className="relative py-12 md:py-16">
        <div className="container mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#E8FBF3] text-[#1AA66C] text-xs font-semibold mb-4">
                <Truck className="w-3.5 h-3.5" />
                {type === "region" && "Région"}
                {type === "department" && "Département"}
                {type === "city" && "Ville"}
                {page.companies_count > 0 && ` · ${page.companies_count} entreprises`}
              </div>
              <h1 className="font-heading font-extrabold text-4xl md:text-5xl lg:text-6xl tracking-tight text-[#2C3E50] leading-tight mb-5" data-testid="seo-h1">
                {page.h1}
              </h1>
              <p className="text-lg text-[#6B7280] max-w-2xl mb-6">
                Comparez gratuitement des devis de déménagement
                {type === "city" ? ` à ${ctx.city}` : type === "department" ? ` dans le ${ctx.dept_name}` : ` en ${ctx.region_name}`}.
                Service gratuit, sans engagement, mise en relation avec des entreprises partenaires.
              </p>
              <form onSubmit={startQuote} className="bg-white border-2 border-[#E5E7EB] rounded-3xl p-4 md:p-5 shadow-sm max-w-2xl" data-testid="seo-quote-widget">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                  <AddressAutocomplete value={departure} onChange={setDeparture} placeholder="Adresse de départ" className="input-clean !pl-11" iconClassName="left-4" testid="seo-departure" />
                  <AddressAutocomplete value={arrival} onChange={setArrival} placeholder="Adresse d'arrivée" className="input-clean !pl-11" iconClassName="left-4" testid="seo-arrival" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="relative md:col-span-2">
                    <Calendar className="w-4 h-4 text-[#2BD391] absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none z-10" />
                    <input
                      type="date"
                      value={movingDate}
                      onChange={(e) => setMovingDate(e.target.value)}
                      className="input-clean !pl-11 !text-sm w-full"
                      data-testid="seo-date"
                    />
                  </div>
                  <button type="submit" className="btn-primary w-full !text-xs whitespace-nowrap" data-testid="seo-cta-btn">
                    Comparer les devis
                  </button>
                </div>
              </form>
            </div>
            <aside className="lg:col-span-5 space-y-4">
              <div className="rounded-3xl border border-[#E5E7EB] bg-gradient-to-br from-[#F7F9FB] to-white p-6">
                <div className="eyebrow mb-3">Le saviez-vous ?</div>
                <ul className="space-y-3 text-sm text-[#2C3E50]">
                  <li className="flex items-start gap-3"><ShieldCheck className="w-5 h-5 text-[#2BD391] mt-0.5 shrink-0" /> <span>Service 100 % gratuit pour les particuliers.</span></li>
                  <li className="flex items-start gap-3"><Users className="w-5 h-5 text-[#2BD391] mt-0.5 shrink-0" /> <span>Mise en relation avec des <strong>entreprises partenaires</strong> uniquement.</span></li>
                  <li className="flex items-start gap-3"><Package className="w-5 h-5 text-[#2BD391] mt-0.5 shrink-0" /> <span>Devis personnalisés selon votre volume et votre trajet.</span></li>
                </ul>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* CORPS DE TEXTE */}
      <section className="py-8 md:py-12 bg-white">
        <div className="container mx-auto px-6 lg:px-10 max-w-4xl">
          <div className="prose-clean space-y-6" data-testid="seo-body">
            {(page.body_paragraphs || []).map((p, i) => (
              <p key={i} className="text-[#2C3E50] leading-relaxed text-[15px]">{p}</p>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      {(page.services?.length > 0) && (
        <section className="py-10 md:py-14 bg-[#F7F9FB]">
          <div className="container mx-auto px-6 lg:px-10">
            <div className="eyebrow mb-4">Services proposés</div>
            <h2 className="font-heading font-extrabold text-3xl md:text-4xl text-[#2C3E50] mb-8">Prestations disponibles</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {page.services.map((s) => (
                <div key={s} className="bg-white border border-[#E5E7EB] rounded-2xl px-4 py-4 text-sm font-semibold text-[#2C3E50] text-center" data-testid={`service-${s}`}>
                  {s}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* MAILLAGE INTERNE */}
      {type === "region" && (
        <SectionLinks
          title={`Départements de ${ctx.region_name}`}
          eyebrow="Choisir un département"
          items={(page.departments || []).map((code) => ({
            to: `/demenagement/${page.region_slug}/${deptUrlPart(code)}`,
            label: DEPT_LABELS[code] || code,
            hint: `Département ${code}`,
          }))}
          testid="dept-links"
        />
      )}
      {type === "department" && (
        <>
          {(page.cities_in_dept?.length > 0) && (
            <SectionLinks
              title="Villes du département"
              eyebrow="Choisir une ville"
              items={page.cities_in_dept.map((cs) => ({
                to: `/demenagement/${page.region_slug}/${page.dept_slug_numero}/${cs}`,
                label: cs.replace(/-/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()),
              }))}
              testid="city-links"
            />
          )}
          {(page.neighbor_depts?.length > 0) && (
            <SectionLinks
              title="Départements voisins"
              eyebrow="Étendre votre recherche"
              items={page.neighbor_depts.map((code) => ({
                to: `/demenagement/${page.region_slug}/${deptUrlPart(code)}`,
                label: DEPT_LABELS[code] || code,
                hint: `Département ${code}`,
              }))}
              testid="neighbor-links"
            />
          )}
        </>
      )}
      {type === "city" && (page.nearby_cities?.length > 0) && (
        <SectionLinks
          title="Villes proches"
          eyebrow="Autres communes"
          items={page.nearby_cities.map((cs) => ({
            to: `/demenagement/${page.region_slug}/${page.dept_slug_numero}/${cs}`,
            label: cs.replace(/-/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()),
          }))}
          testid="nearby-links"
        />
      )}

      {/* FAQ */}
      {(page.faq?.length > 0) && (
        <section className="py-14 md:py-20 bg-white">
          <div className="container mx-auto px-6 lg:px-10 max-w-4xl">
            <div className="eyebrow mb-3">Questions fréquentes</div>
            <h2 className="font-heading font-extrabold text-3xl md:text-4xl text-[#2C3E50] mb-8">FAQ locale</h2>
            <div className="space-y-3" data-testid="faq-list">
              {page.faq.map((item, i) => (
                <details key={i} className="group bg-[#F7F9FB] border border-[#E5E7EB] rounded-2xl overflow-hidden" data-testid={`faq-item-${i}`}>
                  <summary className="cursor-pointer p-5 flex items-start justify-between gap-4 font-semibold text-[#2C3E50] hover:bg-[#E8FBF3] transition-colors">
                    <span>{item.q}</span>
                    <ChevronRight className="w-4 h-4 shrink-0 mt-1 group-open:rotate-90 transition-transform text-[#2BD391]" />
                  </summary>
                  <div className="px-5 pb-5 text-sm text-[#5C6B7A] leading-relaxed">{item.a}</div>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* MENTION LÉGALE ANNUAIRE */}
      <section className="py-8 bg-[#F7F9FB]">
        <div className="container mx-auto px-6 lg:px-10 max-w-4xl">
          <div className="bg-white border border-[#E5E7EB] rounded-2xl p-5 text-xs text-[#6B7280] leading-relaxed" data-testid="seo-conformity">
            <div className="flex items-start gap-3">
              <Info className="w-4 h-4 text-[#2BD391] shrink-0 mt-0.5" />
              <p>
                <strong className="text-[#2C3E50]">Déménageur Pro est une plateforme de mise en relation.</strong>{" "}
                Les demandes de devis sont transmises uniquement à des entreprises partenaires ou autorisées à les recevoir.
                Certaines entreprises affichées dans l'annuaire peuvent être <em>référencées à titre informatif</em> à partir
                de données professionnelles publiques : leur présence dans l'annuaire ne signifie pas qu'elles sont
                partenaires.{" "}
                <Link to="/mentions-legales-b2b" className="text-[#1AA66C] font-semibold hover:underline">
                  En savoir plus
                </Link>
                .
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-6 lg:px-10">
          <div className="bg-gradient-to-br from-[#2BD391] to-[#1AA66C] rounded-3xl p-10 md:p-14 text-center relative overflow-hidden">
            <h2 className="font-heading font-extrabold text-white text-3xl md:text-4xl tracking-tight mb-3" style={{ color: "white" }}>
              Prêt à recevoir vos devis ?
            </h2>
            <p className="text-white/90 max-w-xl mx-auto mb-7">Un seul formulaire, plusieurs devis auprès de nos entreprises partenaires. Gratuit et sans engagement.</p>
            <Link to="/devis" className="inline-flex items-center gap-2 bg-white text-[#1AA66C] hover:bg-[#1A2A3A] hover:text-white rounded-full px-8 py-4 font-bold text-sm uppercase tracking-wider transition-all" data-testid="seo-final-cta">
              Demander un devis gratuit <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────

const DEPT_LABELS = {
  "75": "Paris (75)",
  "77": "Seine-et-Marne (77)",
  "78": "Yvelines (78)",
  "91": "Essonne (91)",
  "92": "Hauts-de-Seine (92)",
  "93": "Seine-Saint-Denis (93)",
  "94": "Val-de-Marne (94)",
  "95": "Val-d'Oise (95)",
};

const DEPT_URL_SLUGS = {
  "75": "paris-75", "77": "seine-et-marne-77", "78": "yvelines-78",
  "91": "essonne-91", "92": "hauts-de-seine-92", "93": "seine-saint-denis-93",
  "94": "val-de-marne-94", "95": "val-d-oise-95",
};

function deptUrlPart(code) {
  return DEPT_URL_SLUGS[code] || code;
}

function setMetaTag(name, content) {
  if (!content) return;
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLinkTag(rel, href) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function injectSchema(page) {
  // Nettoyer les anciens
  document.querySelectorAll('script[data-seo-schema]').forEach((n) => n.remove());
  const url = `${window.location.origin}${page.slug_path}`;
  const graph = [];

  // BreadcrumbList
  const items = [{ "@type": "ListItem", position: 1, name: "Accueil", item: `${window.location.origin}/` }];
  if (page.region_slug) items.push({ "@type": "ListItem", position: 2, name: page.ctx?.region_name || "Région", item: `${window.location.origin}/demenagement/${page.region_slug}` });
  if (page.dept_slug_numero) items.push({ "@type": "ListItem", position: 3, name: page.ctx?.dept_name || "Département", item: `${window.location.origin}/demenagement/${page.region_slug}/${page.dept_slug_numero}` });
  if (page.city_slug) items.push({ "@type": "ListItem", position: 4, name: page.ctx?.city || "Ville", item: url });
  graph.push({ "@type": "BreadcrumbList", itemListElement: items });

  // FAQPage
  if (page.faq?.length) {
    graph.push({
      "@type": "FAQPage",
      mainEntity: page.faq.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
  }

  // Service (pour ville et département)
  if (page.type !== "region") {
    graph.push({
      "@type": "Service",
      serviceType: "Déménagement",
      provider: { "@type": "Organization", name: "Déménageur Pro", url: window.location.origin },
      areaServed: {
        "@type": "AdministrativeArea",
        name: page.ctx?.city || page.ctx?.dept_name || page.ctx?.region_name,
      },
      description: page.meta_description,
      offers: { "@type": "Offer", price: "0", priceCurrency: "EUR", description: "Devis gratuit" },
    });
  }

  const script = document.createElement("script");
  script.type = "application/ld+json";
  script.setAttribute("data-seo-schema", "1");
  script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
  document.head.appendChild(script);
}

const SectionLinks = ({ title, eyebrow, items, testid }) => (
  <section className="py-10 md:py-14 bg-white" data-testid={testid}>
    <div className="container mx-auto px-6 lg:px-10">
      <div className="eyebrow mb-3">{eyebrow}</div>
      <h2 className="font-heading font-extrabold text-2xl md:text-3xl text-[#2C3E50] mb-6">{title}</h2>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {items.map((it) => (
          <Link
            key={it.to}
            to={it.to}
            className="group flex items-start justify-between gap-3 px-5 py-4 rounded-2xl bg-[#F7F9FB] hover:bg-[#E8FBF3] border border-transparent hover:border-[#2BD391] transition-all"
            data-testid={`link-${it.to.replace(/\//g, "_")}`}
          >
            <div>
              <div className="font-heading font-bold text-sm text-[#2C3E50]">{it.label}</div>
              {it.hint && <div className="text-[10px] text-[#6B7280] mt-0.5">{it.hint}</div>}
            </div>
            <ArrowRight className="w-4 h-4 text-[#6B7280] group-hover:text-[#2BD391] group-hover:translate-x-0.5 transition-all mt-0.5 shrink-0" />
          </Link>
        ))}
      </div>
    </div>
  </section>
);
